import { formatMoneyShowZeroPrice } from '@/modules/product/helpers'
import { QuoteSorts } from '@/modules/quote/types'
import useQuotes from '@/modules/quote/useQuotes'
import useQuotesFilter from '@/modules/quote/useQuotesFilter'
import { n, t } from '@/plugins/i18n'
import { SortDirection, ViewToggle } from '@/types'
import { Discount, Quote, QuoteActivityType, QuoteStatus } from '@/types/index'
import { mdiHistory, mdiMessageText } from '@mdi/js'
import { ComputedRef, computed } from 'vue'

const formatDiscount = (discount: Discount): string => {
  if (discount.percentual) {
    return t('discount', [n(discount.value.value) + '%'])
  } else {
    return t('discount', [formatMoneyShowZeroPrice(discount.value, false)])
  }
}

const getQuoteStatusColor = (status: QuoteStatus): string => {
  switch (status) {
    case QuoteStatus.ACCEPTED:
      return 'green'
    case QuoteStatus.DRAFT:
    case QuoteStatus.REQUESTED:
      return 'grey'
    case QuoteStatus.NEGOTIATION:
      return 'blue'
    case QuoteStatus.REJECTED:
    default:
      return 'red'
  }
}

const useQuoteSortToggle = (): {
  direction: ComputedRef<SortDirection>
  toggleSort: (page: number, keyword: string) => void
} => {
  const sort = computed({
    get: () => useQuotesFilter().selection.sort,
    set(sort) {
      useQuotesFilter().selection.sort = sort
    }
  })

  const toggleSort = (page: number, keyword: string) => {
    if (sort.value == QuoteSorts.DATE_ASC) {
      sort.value = QuoteSorts.DATE_DESC
    } else if (sort.value == QuoteSorts.DATE_DESC) {
      sort.value = QuoteSorts.DATE_ASC
    } else {
      sort.value = QuoteSorts.DATE_ASC
    }
    useQuotes().getQuotes(page, keyword)
  }

  const direction = computed(() => {
    if (sort.value == QuoteSorts.DATE_ASC) {
      return SortDirection.ASC
    } else if (sort.value == QuoteSorts.DATE_DESC) {
      return SortDirection.DESC
    } else {
      return SortDirection.NONE
    }
  })

  return { direction, toggleSort }
}

const isQuoteInFinalStage = (quote: Quote): boolean => {
  return quote.quoteStatus === QuoteStatus.ACCEPTED || quote.quoteStatus === QuoteStatus.REJECTED
}

const getActivityViews = (): ViewToggle<QuoteActivityType>[] => [
  {
    name: QuoteActivityType.CHAT,
    altText: t('viewToggle.quoteActivityChat'),
    icon: mdiMessageText,
    restriction: () => true,
    showOnMobile: true
  },
  {
    name: QuoteActivityType.HISTORY,
    altText: t('viewToggle.quoteActivityHistory'),
    icon: mdiHistory,
    restriction: () => true,
    showOnMobile: true
  }
]

export {
  formatDiscount,
  getActivityViews,
  getQuoteStatusColor,
  isQuoteInFinalStage,
  useQuoteSortToggle
}
